import { FC, PropsWithChildren } from 'react';
import { alpha, Box } from '@mui/material';
import { Global } from '@emotion/react';
import { defaultCommunityColor } from '@sweatpals/react-ui-kit';
import { UserDataType } from '@/modules/_core/types/user-types';

export interface EmbedEventCalendarStylesWrapperProps {
	isMobileView?: boolean;
	isLoading?: boolean;
	hostData?: UserDataType;
	primaryColorHex?: string;
	popupBackgroundColorHex?: string;
	headerFontColorHex?: string;
	fontFamily?: string;
}

export const EventCalendarStylesWrapper: FC<PropsWithChildren<EmbedEventCalendarStylesWrapperProps>> = props => {
	const { children, isMobileView, isLoading, hostData, primaryColorHex, popupBackgroundColorHex, headerFontColorHex, fontFamily } = props;

	const finalFontFamily = [fontFamily, 'Montserrat', 'sans-serif'].filter(Boolean).join(', ');

	const hostColor = hostData?.primaryColor || defaultCommunityColor;
	const primaryColor = primaryColorHex ? `#${primaryColorHex}` : hostColor;
	const headerFontColor = headerFontColorHex ? `#${headerFontColorHex}` : primaryColor;

	const headerCellBackgroundColor = alpha(primaryColor, 0.1);
	const buttonHoverBackgroundColor = alpha(primaryColor, 0.1);

	const dateCellFontColor = alpha(primaryColor, 1);
	const dateCellBackgroundColor = alpha(primaryColor, 0.05);
	const dateCellHoverBorderColor = alpha(primaryColor, 0.3);

	const inactiveDateCellFontColor = alpha(primaryColor, 0.6);
	const inactiveDateCellBackgroundColor = alpha(primaryColor, 0.025);
	const inactiveDateCellHoverBorderColor = alpha(primaryColor, 0);

	const inactiveDateCellDateOpacity = 0.4;
	const inactiveDateCellContentOpacity = 0.3;

	const tableBorderSpacing = isMobileView ? 8 : 6;
	const headerBottomBorderSpacing = 8;

	const headerMargin = '16px';

	return (
		<>
			<Global
				styles={{
					':root': {
						'--fc-host-color': hostColor,
						'--fc-primary-color': primaryColor,
						'--fc-font-size': '16px',
						'--fc-font-color': dateCellFontColor,
						'--fc-font-family': finalFontFamily,
						'--fc-header-font-color': headerFontColor,
						'--fc-date-cell-bg-color': dateCellBackgroundColor,
						'--fc-date-cell-hover-border-color': isLoading ? 'initial' : dateCellHoverBorderColor,
						'--fc-header-cell-bg-color': headerCellBackgroundColor,
						'--fc-inactive-date-cell-bg-color': inactiveDateCellBackgroundColor,
						'--fc-inactive-date-cell-font-color': inactiveDateCellFontColor,
						'--fc-inactive-date-cell-hover-border-color': isLoading ? 'initial' : inactiveDateCellHoverBorderColor,
						'--fc-inactive-date-cell-content-opacity': inactiveDateCellContentOpacity,
						'--fc-inactive-date-cell-date-opacity': inactiveDateCellDateOpacity,
						'--fc-table-border-spacing': `${tableBorderSpacing}px`,
						'--fc-header-bottom-border-spacing': `${headerBottomBorderSpacing}px`,
						'--fc-popup-bg-color': popupBackgroundColorHex ? `#${popupBackgroundColorHex}` : '#ffffff',

						// FullCalendar css vars
						// https://github.com/fullcalendar/fullcalendar/blob/main/packages/core/src/styles/vars.css

						'--fc-page-bg-color': 'transparent',
						'--fc-neutral-bg-color': headerCellBackgroundColor,
						// '--fc-neutral-text-color':'#808080',
						'--fc-border-color': 'transparent',

						'--fc-button-text-color': 'var(--fc-primary-color)',
						'--fc-button-bg-color': 'transparent',
						'--fc-button-border-color': 'transparent',
						'--fc-button-hover-bg-color': isLoading ? 'initial' : buttonHoverBackgroundColor,
						'--fc-button-hover-border-color': isLoading ? 'initial' : 'transparent',
						'--fc-button-active-bg-color': buttonHoverBackgroundColor,
						'--fc-button-active-border-color': 'transparent',

						'--fc-event-bg-color': 'transparent',
						'--fc-event-border-color': 'var(--fc-host-color)',
						'--fc-event-text-color': 'var(--fc-font-color)',
						// '--fc-event-selected-overlay-color': 'rgba(0, 0, 0, 0.25)',

						// '--fc-more-link-bg-color': '#d0d0d0',
						// '--fc-more-link-text-color': 'inherit',

						// '--fc-event-resizer-thickness': '8px',
						// '--fc-event-resizer-dot-total-width': '8px',
						// '--fc-event-resizer-dot-border-width': '1px',

						// '--fc-non-business-color': 'rgba(215, 215, 215, 0.3)',
						// '--fc-bg-event-color': 'rgb(143, 223, 130)',
						// '--fc-bg-event-opacity': '0.3',
						// '--fc-highlight-color': 'rgba(188, 232, 241, 0.3)',
						// '--fc-today-bg-color': 'rgba(255, 220, 40, 0.15)',
						'--fc-today-bg-color': 'transparent',
						// '--fc-now-indicator-color': 'red',
					},
				}}
			/>
			<Box
				sx={{
					color: 'var(--fc-font-color)',
					fontSize: 'var(--fc-font-size)',
					fontFamily: 'var(--fc-font-family)',
					backgroundColor: 'var(--fc-page-bg-color)',

					'& .fc': {
						width: isMobileView ? '100%' : '1250px',
						height: isMobileView ? 'auto' : '1300px',
						opacity: isLoading ? 0.5 : 1,

						'& .fc-scrollgrid-section-header .fc-scroller': {
							overflow: 'hidden !important',
						},

						'& .fc-header-toolbar': {
							mb: headerMargin,
						},

						'& .fc-prev-button, & .fc-next-button': {
							padding: '3.5px 4px',
							cursor: isLoading ? 'default' : 'pointer',
						},

						'& .fc-today-button': {
							py: '4px',
							cursor: isLoading ? 'default' : 'pointer',
						},

						'& .fc-empty-button': {
							minWidth: isMobileView ? '1px' : '100px',
							padding: isMobileView ? '0px' : undefined,
							cursor: 'default',
							backgroundColor: 'transparent',

							'&:hover': {
								backgroundColor: 'transparent',
							},
						},

						'& .fc-toolbar-title': {
							fontFamily: 'Integral CF, sans-serif',
							fontWeight: '500',
							fontSize: '1.15em',
						},

						'& .fc-day-past': {
							'& .fc-daygrid-day-top': {
								opacity: 'var(--fc-inactive-date-cell-date-opacity)',
							},

							'& .fc-daygrid-day-events': {
								opacity: 'var(--fc-inactive-date-cell-content-opacity)',
							},
						},

						'& .fc-day-future': {
							'& .fc-daygrid-day-top': {
								opacity: 1,
							},
						},

						'& .fc-daygrid-day-events': {
							px: '12px',
						},

						'& .fc-daygrid-day *:before, & .fc-daygrid-day *:after': {
							borderSpacing: '0',
						},

						'.fc-daygrid-event': {
							cursor: isLoading ? 'default' : 'pointer',

							'&:hover': {
								backgroundColor: isLoading ? 'initial' : buttonHoverBackgroundColor,
							},
						},

						'& .fc-daygrid-day-number': {
							padding: '12px 12px 8px 8px',
							fontWeight: '600',
						},

						'& td, & th': {
							border: 'none',
							// width: '170px',
						},

						'& .fc-button, & .fc-button:focus': {
							outline: 'none !important',
							border: 'none !important',
							boxShadow: 'none !important',
						},

						'& .fc-scrollgrid-sync-inner': {
							textAlign: 'left',
							// width: '170px',
						},

						'& .fc-col-header-cell-cushion': {
							color: 'var(--fc-header-font-color)',
							padding: '12px',
							fontFamily: 'Integral CF, sans-serif',
							fontWeight: '600',
							fontSize: '0.75em',
						},

						'& .fc-daygrid-day-frame': {
							backgroundColor: 'var(--fc-date-cell-bg-color)',
							borderRadius: '10px',
							minHeight: '193px',
							// width: '170px',
						},

						'& .fc-col-header-cell': {
							backgroundColor: 'var(--fc-header-cell-bg-color)',
							borderRadius: '4px',
						},

						'& .fc-day-today': {
							borderRadius: '10px',
						},

						'& .fc-scrollgrid-sync-table': {
							borderCollapse: 'separate',
							borderSpacing: `var(--fc-table-border-spacing)`,
							tableLayout: 'fixed',
						},

						'& .fc-col-header': {
							borderCollapse: 'separate',
							borderSpacing: `var(--fc-table-border-spacing) 0`,
							tableLayout: 'fixed',
						},

						'& .fc-scrollgrid': {
							border: 'none',
							borderSpacing: `0px ${headerBottomBorderSpacing - tableBorderSpacing}px`,
							borderCollapse: 'separate',
							tableLayout: 'fixed',
						},

						'& .fc-more-popover': {
							backgroundColor: 'var(--fc-popup-bg-color)',
						},

						'& .fc-list-empty': {
							backgroundColor: 'transparent',

							'& .fc-list-empty-cushion': {
								fontFamily: 'Integral CF, sans-serif',
							},
						},

						'& .fc-listMonth-view': {
							paddingRight: '6px',

							'& .fc-scroller': {
								'&::-webkit-scrollbar': {
									width: '5px',
								},

								'&::-webkit-scrollbar-thumb': {
									borderRadius: '2px',
									backgroundColor: 'rgba(250, 250, 250, 0.7)',
								},
							},
						},

						'& .fc-view-harness': {
							minHeight: '650px',
						},

						'& .fc-list-table': {
							borderCollapse: 'separate',
							borderSpacing: `0 var(--fc-table-border-spacing)`,
							width: '100%',
							tableLayout: 'fixed',
							marginTop: `-${headerMargin}`, // header spacing

							'& thead': {
								position: 'static',
								'& tr th': {
									fontSize: '0px',

									'&:nth-child(1)': {
										boxSizing: 'border-box',
										width: 'calc(72px + 12px)',
									},
									'&:nth-child(2)': {
										width: 'calc(10px + 8px)',
									},
									'&:nth-child(3)': {
										width: '100%',
									},
								},
							},

							'& tbody tr td': {
								'&:nth-child(1)': {
									boxSizing: 'border-box',
									whiteSpace: 'normal',
									verticalAlign: 'middle',

									py: '14px',
									px: '4px',
									pl: '12px',
									borderTopLeftRadius: '10px',
									borderBottomLeftRadius: '10px',
								},
								'&:nth-child(2)': {
									py: '14px',
									px: '4px',
									verticalAlign: 'middle',
								},
								'&:nth-child(3)': {
									verticalAlign: 'middle',
									py: '14px',
									px: '4px',
									pr: '12px',
									borderTopRightRadius: '10px',
									borderBottomRightRadius: '10px',
								},
							},

							'& tbody tr th': {
								// position: 'sticky', // background color with transparency is overlapping when scrolling in stick mode
								position: 'static',
							},

							'& .fc-list-day': {
								borderSpacing: 'initial',

								'& .fc-list-day-cushion': {
									padding: '12px',
									borderRadius: '4px',
									backgroundColor: 'var(--fc-header-cell-bg-color)',

									'& .fc-list-day-text, & .fc-list-day-side-text': {
										fontFamily: 'Integral CF, sans-serif',
										fontSize: '12px',
										fontWeight: '600',
										lineHeight: 'normal',
										color: 'var(--fc-header-font-color)',
									},
								},
							},

							'& .fc-list-event': {
								cursor: isLoading ? 'default' : 'pointer',
								backgroundColor: 'var(--fc-date-cell-bg-color)',

								'& td.fc-list-event-time': {
									fontFamily: 'Montserrat, sans-serif',
									fontSize: '14px',
									fontWeight: '500',
									lineHeight: '1.2em',
									whiteSpace: 'normal',
								},

								'& td.fc-list-event-title': {
									whiteSpace: 'nowrap',
									overflow: 'hidden',
									textOverflow: 'ellipsis',
								},

								'&:hover td': {
									backgroundColor: isLoading ? 'initial' : buttonHoverBackgroundColor,
								},
							},
						},
					}
				}}
			>
				{children}
			</Box>
		</>
	);
};
